import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import {
  VERSION_AUTO_RELOAD_SEC,
  VERSION_DISMISS_KEY,
  VERSION_RELOAD_GUARD_KEY,
  VERSION_SNOOZE_MS,
} from '@/constants/version';
import { useVersionCheck } from '@/hooks/useVersionCheck';
import type { BuildInfo } from '@/types/version';

function readDismissedAt(): number | null {
  try {
    const raw = localStorage.getItem(VERSION_DISMISS_KEY);
    if (!raw) {
      return null;
    }
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  } catch {
    return null;
  }
}

function isSnoozeActive(): boolean {
  const at = readDismissedAt();
  if (at == null) {
    return false;
  }
  return Date.now() - at < VERSION_SNOOZE_MS;
}

export const VersionUpdateNotification: React.FC = () => {
  const { hasUpdate, serverVersion, updateCurrentVersion, checkVersion } = useVersionCheck();
  const [open, setOpen] = useState(false);
  const [countdown, setCountdown] = useState(VERSION_AUTO_RELOAD_SEC);
  const countdownRef = useRef(VERSION_AUTO_RELOAD_SEC);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const performReload = useCallback(
    (info: BuildInfo) => {
      try {
        sessionStorage.setItem(VERSION_RELOAD_GUARD_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }
      updateCurrentVersion(info);
      window.location.reload();
    },
    [updateCurrentVersion]
  );

  useEffect(() => {
    if (!hasUpdate || !serverVersion) {
      setOpen(false);
      return;
    }
    if (isSnoozeActive()) {
      setOpen(false);
      return;
    }
    setOpen(true);
    setCountdown(VERSION_AUTO_RELOAD_SEC);
    countdownRef.current = VERSION_AUTO_RELOAD_SEC;
  }, [hasUpdate, serverVersion]);

  useEffect(() => {
    if (!open || !serverVersion) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      countdownRef.current -= 1;
      setCountdown(countdownRef.current);
      if (countdownRef.current <= 0 && serverVersion) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        performReload(serverVersion);
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [open, serverVersion, performReload]);

  const handleSnooze = () => {
    try {
      localStorage.setItem(VERSION_DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    setOpen(false);
    window.setTimeout(() => {
      try {
        localStorage.removeItem(VERSION_DISMISS_KEY);
      } catch {
        /* ignore */
      }
      void checkVersion(true);
    }, VERSION_SNOOZE_MS);
  };

  const handleUpdateNow = () => {
    if (serverVersion) {
      performReload(serverVersion);
    }
  };

  if (!serverVersion) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleSnooze} disableEscapeKeyDown maxWidth="sm" fullWidth>
      <DialogTitle>Nova versão disponível</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          Uma nova versão do Kaive foi publicada. Atualize para carregar as últimas correções e melhorias e evitar
          dados desatualizados na tela.
        </DialogContentText>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Atualização automática em {countdown}s
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleSnooze} color="inherit">
          Adiar
        </Button>
        <Button onClick={handleUpdateNow} variant="contained" color="primary">
          Atualizar agora
        </Button>
      </DialogActions>
    </Dialog>
  );
};
